/**
 * Upcoming deadlines across a learner's courses — ONE request, from our own endpoint.
 *
 * ------------------------------------------------------------------- what changed ----
 * This file used to call the platform's dates API, `GET /api/course_home/dates/{course_id}`,
 * ONCE PER ENROLLED COURSE and merge the results here. That endpoint is per-course by
 * design — `frontend-app-learning` calls it from a page about one course, which is correct
 * for that page — and a dashboard panel spanning every enrolment inherited a fan-out that
 * grew with the number of courses a learner was taking.
 *
 * `GET /api/pathways/v1/learner/dates` answers the whole question once. It runs the
 * platform's own `get_course_assignments` per course IN PROCESS, which is the same work
 * without N HTTP round trips queueing against a two-worker uwsgi pool — the same fix, and
 * the same reasoning, as the pathway-progress change in `openedx_thinlms_pathways/completion.py`.
 *
 * THE FILTERING MOVED WITH IT, and none of it was lost. Everything the four functions below
 * used to drop client-side is now dropped before serialisation:
 *
 *   informational dates   course start/end, enrolment start/end, certificate availability,
 *                         both upgrade deadlines — the server returns only graded
 *                         subsections, which is the ONLY thing that ever survived the
 *                         `NOT_A_DEADLINE` set below
 *   completed work        `assignment.complete`
 *   the past              `assignment.date < now`
 *   unreachable work      an assignment whose subsection has not opened has no `url`, which
 *                         is the platform's own way of saying the learner cannot reach it
 *
 * THE COURSE NAME IS NEW. The per-course payload did not carry it, so the merge here set
 * `courseName: ''` and the panel printed a deadline with nothing beside it. The server
 * joins `CourseOverview.display_name` in.
 *
 * The label formatting stays here, because "Due tomorrow" is a rendering decision that
 * depends on the reader's clock and timezone, not the server's.
 */

import { openedx } from './openedx';

/** One deadline, as the server sends it. */
export interface WireDeadline {
  course_id: string;
  course_title: string;
  title: string;
  /** ISO 8601, always in the future at the moment the server answered. */
  date: string;
  /** The grading policy bucket, e.g. 'Homework'. Null when the author set none. */
  assignment_type: string | null;
  /** A `jump_to` link into the courseware. */
  link: string;
}

interface WireDeadlines {
  count: number;
  /** How many of the learner's courses were actually examined. */
  courses: number;
  /** True when the learner has more courses than the reader will look at in one request. */
  truncated: boolean;
  results: WireDeadline[];
}

/** A deadline as the panel shows it. */
export interface Deadline {
  courseId: string;
  courseName: string;
  title: string;
  date: string;
  due: string;
  /** Within the next three days — the panel marks these. */
  soon: boolean;
}

const DAY = 24 * 60 * 60 * 1000;

/** "Due tomorrow" reads better than a date, and only for the cases where it is true. */
function dueLabel(date: Date, now: Date): string {
  const days = Math.round((date.getTime() - now.getTime()) / DAY);
  if (days <= 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days <= 7) return `Due in ${days} days`;
  return `Due ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/**
 * Every upcoming deadline in this academy, soonest first.
 *
 * The server has already sorted and filtered; this turns each row into the labelled shape
 * the panel renders. The `date < now` guard is kept because a page left open overnight
 * would otherwise still be showing yesterday's deadline as though it were due.
 */
export async function upcomingDeadlines(now: Date = new Date()): Promise<Deadline[]> {
  const response = await openedx.get<WireDeadlines>('/api/pathways/v1/learner/dates');

  return (response.results ?? [])
    .flatMap((row) => {
      const when = new Date(row.date);
      if (Number.isNaN(when.getTime()) || when.getTime() < now.getTime()) return [];
      return [
        {
          courseId: row.course_id,
          courseName: row.course_title,
          title: row.title,
          date: row.date,
          due: dueLabel(when, now),
          soon: when.getTime() - now.getTime() <= 3 * DAY,
        },
      ];
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}
